import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import ClearFiltersButton from "../components/ClearFiltersButton";
import DownloadCsvButton from "../components/DownloadCsvButton";
import { searchIcon } from "../../../assets";
import ReportsService from "../../../services/ReportsService";
import { componentKey } from "../ReportsSlice";
import { getIcdReport } from "../ReportsSaga";
import {
  CSV_HEADERS_ICD,
  ICD_CODE_TYPE_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
  formatReportDateTime,
  toOperationalPeriodWindow,
} from "../ReportConstants";

type IcdReportTabProps = { facilityId: string };

/**
 * ICD-10 / DSM Report.
 * One row per code x facility — which facility has seen / used each diagnosis
 * code, how many times, for how many patients and when it was last recorded.
 *
 * Codes are read from the visit note assessment and the disposition impression
 * diagnosis, so both clinic and EMS flows are covered.
 *
 * With no date filter the report covers every record; an operational period
 * (0700-0700 ET) or a From/To range narrows it. The two are mutually exclusive.
 */
const IcdReportTab = ({ facilityId }: IcdReportTabProps) => {
  const dispatch = useDispatch();
  const { icdReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>,
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [searchedValue, setSearchedValue] = useState("");
  const [codeType, setCodeType] = useState("");
  const [operationalPeriod, setOperationalPeriod] = useState<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const rows: Array<Record<string, any>> = icdReport?.data || [];

  // The only date filter is the operational period (0700-0700 ET); with none
  // selected the report covers every record.
  const dateParams = operationalPeriod
    ? toOperationalPeriodWindow(operationalPeriod)
    : { startDate: undefined, endDate: undefined };

  const queryParams = {
    // The shared facility selector scopes the report through `seenFacility` —
    // the endpoint ignores `facilityId` by design.
    seenFacility: facilityId || undefined,
    codeType: codeType || undefined,
    searchValue: searchedValue?.trim() || undefined,
    startDate: dateParams.startDate,
    endDate: dateParams.endDate,
  };

  useEffect(() => {
    dispatch(
      getIcdReport({
        pageNumber,
        limit: rowPerPage,
        ...queryParams,
      }),
    );
  }, [
    facilityId,
    pageNumber,
    rowPerPage,
    searchedValue,
    codeType,
    operationalPeriod,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setSearchedValue(value?.trim());
    }, 500);
  };

  const isAnyFilterApplied = Boolean(
    searchedValue || codeType || operationalPeriod,
  );

  const handleClearFilters = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setSearchedValue("");
    setCodeType("");
    setOperationalPeriod(null);
    setPageNumber(1);
  };

  const columns = [
    {
      field: "code",
      label: <div>Code</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-weight-500">{row?.code || "-"}</p>
      ),
    },
    {
      field: "codeType",
      label: <div>Type</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.codeType || "-"}</p>
      ),
    },
    {
      field: "description",
      label: <div>Diagnosis</div>,
      renderLogic: (row: Record<string, any>) => (
        <Tooltip arrow placement="bottom-start" title={row?.description || ""}>
          <p className="text-neutral-90 font-normal max-w-[320px] truncate">
            {row?.description || "-"}
          </p>
        </Tooltip>
      ),
    },
    {
      field: "facilityName",
      label: <div>Facility</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.facilityName || "Unassigned"}
        </p>
      ),
    },
    {
      field: "count",
      label: <div>Times Recorded</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-weight-500">{row?.count ?? 0}</p>
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getIcdReport({
      limit: "All",
      ...queryParams,
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => ({
      code: row?.code,
      codeType: row?.codeType,
      description: row?.description,
      facilityName: row?.facilityName || "Unassigned",
      count: row?.count,
    }));
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <SearchSelectDropdown
          freeSolo={true}
          searchedValue={searchedValue}
          onSearchValueChangeCb={handleSearchChange}
          size="small"
          label="Search by Code / Diagnosis"
          fullWidth={"280px"}
          startIcon={
            <img
              alt="search-icon"
              src={searchIcon}
              width={"18px"}
              height={"18px"}
            />
          }
        />
        <div style={{ minWidth: "180px" }}>
          <SelectDropdown
            label="Code Type"
            value={codeType}
            options={ICD_CODE_TYPE_OPTIONS}
            onChangeCb={(e) => {
              setPageNumber(1);
              setCodeType(e.target.value);
            }}
          />
        </div>
        {/* IAP operational period: a single date -> 0700-0700 ET window. */}
        <div style={{ minWidth: "210px" }}>
          <DatePicker
            label="Operational Period (0700 ET)"
            value={operationalPeriod}
            onChangeCb={(value) => {
              setPageNumber(1);
              setOperationalPeriod(value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_ICD}
          filename="icd-dsm-report.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      {/* Totals for the whole filtered set, not just the current page. */}
      {/* {icdReport?.uniqueCodes != null && (
        <div className="text-sm text-neutral-70 mb-2">
          {icdReport?.startDate
            ? `${formatReportDateTime(icdReport.startDate)} – ${formatReportDateTime(icdReport.endDate)}`
            : "All dates"}
          {` · ${icdReport.uniqueCodes} code(s) · ${icdReport.totalDiagnoses} recorded`}
        </div>
      )} */}

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No ICD/DSM codes found"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: icdReport?.totalPages,
          totalCount: icdReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />
    </div>
  );
};

export default IcdReportTab;
